import {
  Add as AddIcon,
  Remove as RemoveIcon,
} from '../icons';
import React from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Card
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { IMAGE_PLACEHOLDER } from '../../config';
import { useCurrencyFormatter } from '../../contexts/CurrencyContext';
import { usePublicTheme } from '../../contexts/PublicThemeContext';
import { useCart } from '../../contexts/CartContext';
import { useNotification } from '../../contexts/NotificationContext';
import { getHoverColor, hexToRgba } from '../../utils/colorUtils';
import OptimizedImage from '../common/OptimizedImage';

const StyledListItem = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'primaryColor' && prop !== 'disabled',
})(({ theme, primaryColor, disabled }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  cursor: disabled ? 'default' : 'pointer',
  borderRadius: theme.spacing(2.5),
  background: disabled ? 'rgba(245, 245, 245, 0.95)' : 'rgba(255, 255, 255, 0.95)',
  border: disabled ? '1px solid rgba(0, 0, 0, 0.08)' : '1px solid rgba(0, 0, 0, 0.06)',
  backdropFilter: 'blur(10px)',
  boxShadow: disabled ? '0 4px 12px rgba(0, 0, 0, 0.04)' : '0 10px 30px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  opacity: disabled ? 0.7 : 1,
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3.5),
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(3.5),
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.04) 0%, rgba(102, 126, 234, 0.02) 100%)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  ...(!disabled && {
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 20px 50px rgba(0, 0, 0, 0.14), 0 8px 20px rgba(0, 0, 0, 0.10)',
      border: `1px solid ${hexToRgba(primaryColor, 0.2)}`,
      '&::before': {
        opacity: 1,
      },
      '& .menu-image': {
        transform: 'scale(1.1)',
      },
      [theme.breakpoints.up('md')]: {
        transform: 'translateY(-5px) translateX(8px)',
      },
    },
    '&:active': {
      transform: 'translateY(-2px)',
      [theme.breakpoints.up('md')]: {
        transform: 'translateY(-3px) translateX(4px)',
      },
    },
  }),
  '&:last-child': {
    marginBottom: 0,
  },
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  width: 90,
  height: 90,
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  marginRight: theme.spacing(2),
  flexShrink: 0,
  background: 'linear-gradient(135deg, #f8fafc 0%, #e3e8f0 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08), inset 0 1px 4px rgba(0, 0, 0, 0.04)',
  position: 'relative',
  border: 'none',
  [theme.breakpoints.up('md')]: {
    width: 130,
    height: 130,
    borderRadius: theme.spacing(3),
    marginRight: theme.spacing(3.5),
    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.1), inset 0 2px 6px rgba(0, 0, 0, 0.05)',
  },
}));

const menuImageSx = (theme) => ({
  borderRadius: theme.spacing(2.5),
});

const ContentContainer = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  gap: '8px',
});

const QuantityButton = styled(IconButton)(({ theme }) => ({
  width: 36,
  height: 36,
  borderRadius: theme.spacing(2),
  color: '#fff',
  border: 'none',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  [theme.breakpoints.up('md')]: {
    width: 44,
    height: 44,
    borderRadius: theme.spacing(2.5),
    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
  },
  '&:hover': {
    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
    transform: 'scale(1.15)',
    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.55)',
  },
  '&:active': {
    transform: 'scale(1.08)',
  },
  '&.Mui-disabled': {
    background: '#e2e8f0',
    color: '#a0aec0',
    border: 'none',
  },
}));

const ModernChip = styled(Chip)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontSize: '0.7rem',
  fontWeight: 700,
  height: 24,
  transition: 'all 0.3s ease',
  [theme.breakpoints.up('md')]: {
    borderRadius: theme.spacing(2.5),
    fontSize: '0.85rem',
    height: 34,
  },
  '& .MuiChip-label': {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontWeight: 700,
    padding: '0 8px',
    [theme.breakpoints.up('md')]: {
      padding: '0 12px',
    },
  },
  '&.featured': {
    background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
    color: '#fff',
    border: 'none',
    boxShadow: '0 6px 16px rgba(255, 107, 107, 0.45)',
    '&:hover': {
      transform: 'scale(1.08)',
      boxShadow: '0 8px 20px rgba(255, 107, 107, 0.55)',
    },
  },
  '&.category': {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    border: 'none',
    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.35)',
    '&:hover': {
      transform: 'scale(1.08)',
      boxShadow: '0 8px 20px rgba(102, 126, 234, 0.45)',
    },
  },
  '&.unavailable': {
    background: 'linear-gradient(135deg, #fc7d7d 0%, #f093fb 100%)',
    color: '#fff',
    border: 'none',
    boxShadow: '0 6px 16px rgba(252, 125, 125, 0.45)',
    '&:hover': {
      transform: 'scale(1.08)',
      boxShadow: '0 8px 20px rgba(252, 125, 125, 0.55)',
    },
  },
}));

const MenuListItem = ({
  item,
  onAddToCart,
  onViewDetails,
  showCategory = true,
  orderingDisabled = false
}) => {
  const {
    id,
    name,
    description,
    price,
    image,
    category,
    is_available = true,
    is_featured,
    has_options
  } = item;

  const { formatCurrency } = useCurrencyFormatter();
  const { primaryColor } = usePublicTheme();
  const { getItemQuantity, addItem, items, updateItemQuantity } = useCart();
  const { addNotification } = useNotification();

  // Get current quantity in cart (for items without options)
  const currentQuantity = has_options ? 0 : getItemQuantity(id, []);

  const handleAddToCart = (event) => {
    event.stopPropagation();
    // Always open the dialog to allow special instructions
    onViewDetails(item);
  };

  const handleIncrement = (event) => {
    event.stopPropagation();
    if (!has_options) {
      addItem(item, 1, []);
      const newQuantity = currentQuantity + 1;
      addNotification(`Updated ${name} quantity to ${newQuantity}`, 'primary');
    }
  };

  const handleDecrement = (event) => {
    event.stopPropagation();
    if (!has_options && currentQuantity > 0) {
      // Find the item in cart and update its quantity
      const cartItemIndex = items.findIndex(
        cartItem => cartItem.id === id && JSON.stringify(cartItem.options) === JSON.stringify([])
      );
      if (cartItemIndex !== -1) {
        const newQuantity = currentQuantity - 1;
        updateItemQuantity(cartItemIndex, newQuantity);
        if (newQuantity === 0) {
          addNotification(`Removed ${name} from cart`, 'warning');
        } else {
          addNotification(`Updated ${name} quantity to ${newQuantity}`, 'primary');
        }
      }
    }
  };

  // Render quantity controls or ADD button
  const renderActionButton = () => {
    if (has_options) {
      // Items with options always show the Options button
      return (
        <QuantityButton
          onClick={handleAddToCart}
          disabled={!is_available}
          size="small"
          sx={{
            background: primaryColor || '#667eea',
            boxShadow: `0 2px 8px ${hexToRgba(primaryColor, 0.3)}`,
            '&:hover': {
              background: getHoverColor(primaryColor, 'darken', 15),
              boxShadow: `0 4px 12px ${hexToRgba(primaryColor, 0.4)}`,
            },
          }}
        >
          <AddIcon fontSize="small" />
        </QuantityButton>
      );
    }

    if (currentQuantity === 0) {
      // Show ADD button when quantity is 0
      return (
        <QuantityButton
          onClick={handleAddToCart}
          disabled={!is_available}
          size="small"
          sx={{
            background: primaryColor || '#667eea',
            boxShadow: `0 2px 8px ${hexToRgba(primaryColor, 0.3)}`,
            '&:hover': {
              background: getHoverColor(primaryColor, 'darken', 15),
              boxShadow: `0 4px 12px ${hexToRgba(primaryColor, 0.4)}`,
            },
          }}
        >
          <AddIcon fontSize="small" />
        </QuantityButton>
      );
    }

    // Show quantity controls when quantity > 0
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <QuantityButton
          onClick={handleDecrement}
          disabled={!is_available}
          size="small"
          sx={{
            background: primaryColor || '#667eea',
            boxShadow: `0 2px 8px ${hexToRgba(primaryColor, 0.3)}`,
            '&:hover': {
              background: getHoverColor(primaryColor, 'darken', 15),
              boxShadow: `0 4px 12px ${hexToRgba(primaryColor, 0.4)}`,
            },
          }}
        >
          <RemoveIcon fontSize="small" />
        </QuantityButton>
        <Typography
          variant="body2"
          sx={{
            minWidth: 32,
            textAlign: 'center',
            fontWeight: 800,
            fontSize: '1rem',
            color: '#1f2937',
          }}
        >
          {currentQuantity}
        </Typography>
        <QuantityButton
          onClick={handleIncrement}
          disabled={!is_available}
          size="small"
          sx={{
            background: primaryColor || '#667eea',
            boxShadow: `0 2px 8px ${hexToRgba(primaryColor, 0.3)}`,
            '&:hover': {
              background: getHoverColor(primaryColor, 'darken', 15),
              boxShadow: `0 4px 12px ${hexToRgba(primaryColor, 0.4)}`,
            },
          }}
        >
          <AddIcon fontSize="small" />
        </QuantityButton>
      </Box>
    );
  };

  const isDisabled = !is_available;

  return (
    <StyledListItem
      primaryColor={primaryColor}
      disabled={isDisabled}
      sx={{
        '&::before': {
          background: 'rgba(102, 126, 234, 0.02)',
        },
      }}
      onClick={() => {
        if (!isDisabled) {
          onViewDetails(item);
        }
      }}
      elevation={isDisabled ? 0 : 1}
    >
      <ImageContainer>
        <OptimizedImage
          className="menu-image"
          src={image || IMAGE_PLACEHOLDER}
          alt={name}
          fallbackSrc={IMAGE_PLACEHOLDER}
          sx={(theme) => ({
            ...menuImageSx(theme),
            ...(isDisabled ? { filter: 'grayscale(100%)' } : {}),
          })}
        />
      </ImageContainer>

      <ContentContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5, gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h5"
              component="h3"
              sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 800,
                fontSize: { xs: '0.95rem', sm: '1rem', md: '1.35rem' },
                lineHeight: 1.3,
                color: isDisabled ? '#9ca3af' : '#1f2937',
                letterSpacing: '-0.01em',
                mb: 0.25,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {name}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: { xs: '0.95rem', sm: '1rem', md: '1.25rem' },
                fontWeight: 800,
                color: isDisabled ? '#9ca3af' : '#1f2937',
                letterSpacing: '-0.02em',
              }}
            >
              {formatCurrency(price)}
            </Typography>
          </Box>
          {!isDisabled && !orderingDisabled && (
            <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              {renderActionButton()}
            </Box>
          )}
        </Box>

        {description && (
          <Typography
            variant="body2"
            sx={{
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.5,
              fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.95rem' },
              color: isDisabled ? '#b0b7c3' : '#6b7280',
            }}
          >
            {description}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          {showCategory && category && !isDisabled && (
            <ModernChip
              label={category.name}
              size="small"
              className="category"
              chipType="category"
            />
          )}
          {is_featured && !isDisabled && (
            <ModernChip
              label="Featured"
              size="small"
              className="featured"
            />
          )}
          {isDisabled && (
            <ModernChip
              label="Unavailable"
              size="small"
              className="unavailable"
            />
          )}
        </Box>
      </ContentContainer>

    </StyledListItem>
  );
};

export default MenuListItem;
